import Phaser from 'phaser';
import { PALETTE } from '../config';
import { SaveManager } from '../game/SaveManager';
import { Ads } from '../game/ads';
import { submitScore } from '../game/api';
import { LANGS, getLang, t, type Lang } from '../game/i18n';
import { Button } from '../ui/Button';
import { onResize, viewport } from '../ui/viewport';

/** Hub: branded stadium backdrop, logo, big buttons. */
export class MainMenu extends Phaser.Scene {
  private langSelect?: HTMLSelectElement;
  // One preroll ad on the FIRST GIOCA click of the page session (GameMonetize placement rule).
  private static firstPlayAdShown = false;

  constructor() {
    super('MainMenu');
  }

  /** Enter the game. The very first Play click of the session shows a preroll interstitial. */
  private startPlay(): void {
    const go = (): void => { this.scene.start('DisciplineSelect'); };
    if (MainMenu.firstPlayAdShown) { go(); return; }
    MainMenu.firstPlayAdShown = true;
    Ads.interstitial(go);
  }

  create(): void {
    if (!SaveManager.instance.hasProfile()) {
      this.scene.start('NameEntry');
      return;
    }
    this.build();
    this.syncMeta();
    const w0 = this.scale.width;
    const h0 = this.scale.height;
    onResize(this, () => {
      if (Math.abs(this.scale.width - w0) > 2 || Math.abs(this.scale.height - h0) > 2) this.scene.restart();
    });
  }

  /** Push the player's current coin total to the global board on menu load, so coins earned
   * before the board existed show up — not just future races. (Medals are derived server-side
   * from the per-discipline standings, so there is nothing to sync for them.) */
  private syncMeta(): void {
    const save = SaveManager.instance;
    if (!save.hasProfile()) return;
    void submitScore({
      disciplineId: 'coins', metricKind: 'distance', metric: save.coins, score: 0, rank: 1,
      playerName: save.teamName || 'Team Anonimo', clientId: save.clientId,
      athleteId: '', athleteName: '', species: '', nationality: '', createdAt: Date.now(),
    });
  }

  private build(): void {
    const { w, h, cx, s } = viewport(this);
    // Higgsfield team key art: group celebrates bottom-right, clean sky top-left for the UI.
    this.coverBackground('menu_hero');
    this.leftScrim(w, h);
    this.ambientParticles(w, h);

    // Logo with a soft glow halo + gentle float; pops in on entrance. Smaller + higher than before
    // so the whole action stack fits above the screen bottom on short landscape phones.
    const logoY = h * 0.21;
    const glow = this.add.image(cx, logoY, 'logo').setOrigin(0.5).setTint(0xffe9a8).setAlpha(0.0).setDepth(2);
    const logo = this.add.image(cx, logoY, 'logo').setOrigin(0.5).setDepth(3);
    const logoScale = Math.min((w * 0.5) / logo.width, (h * 0.26) / logo.height);
    logo.setScale(logoScale * 0.6).setAlpha(0);
    glow.setScale(logoScale * 1.06);
    this.tweens.add({ targets: logo, scale: logoScale, alpha: 1, duration: 600, ease: 'Back.out' });
    this.tweens.add({ targets: glow, alpha: 0.32, duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
    this.tweens.add({ targets: [logo, glow], y: logoY - 8 * s, duration: 2600, yoyo: true, repeat: -1, ease: 'Sine.inOut' });

    // Team + coins chips on the top bar (drop in from above). The team chip + its pencil are the
    // account/profile entry point: a cloud tick shows when synced, both open the Account screen
    // (where you change your name AND manage Google sign-in).
    const synced = SaveManager.instance.signedIn;
    const teamLabel = `👥 ${SaveManager.instance.teamName}${synced ? '  ☁' : ''}`;
    const openAccount = (): void => { this.scene.start('Account'); };
    const teamChip = this.chip(16 * s, 14 * s, teamLabel, s, 0, openAccount);
    this.editBadge(teamChip.right + 8 * s, 14 * s + teamChip.height / 2, s, openAccount);
    this.chip(w - 16 * s, 14 * s, `🪙 ${SaveManager.instance.coins}`, s, 1);

    // Language picker (top centre): a native combo, readable on mobile, shows the active language.
    this.languageSelect(s);

    // Action stack: a big GIOCA + a 2×2 grid of secondary actions. Positions are FRACTIONS of the
    // height and heights are clamped to the height, so nothing ever falls off the bottom on short
    // landscape phones (the old fixed +88/+80px offsets overflowed). The block sits left-of-centre
    // and spreads wider horizontally, using more of the screen as requested.
    const colX = w * 0.32;
    const bw = Math.min(440, w * 0.52);
    const playH = Phaser.Math.Clamp(h * 0.145, 56, 92);
    const secH = Phaser.Math.Clamp(h * 0.115, 44, 70);
    const vGap = Phaser.Math.Clamp(h * 0.035, 12, 26);
    // Lay the controls out as ONE cohesive block (big GIOCA above a 2×2 grid) instead of pinning them
    // to fixed height fractions: on a tall desktop window the old 0.47 / 0.70 / 0.87 spacing left a big
    // empty gap, so the buttons looked like they were floating. A shared gap keeps them grouped at any
    // aspect ratio, and the block is clamped so it never falls off short landscape phones.
    const blockH = playH + secH * 2 + vGap * 2;
    const blockTop = Phaser.Math.Clamp(h * 0.40, h * 0.30, h - blockH - h * 0.06);
    const playY = blockTop + playH / 2;
    const row1Y = playY + playH / 2 + vGap + secH / 2;
    const row2Y = row1Y + secH + vGap;
    const play = new Button(this, colX, playY, t('menu.play'), () => this.startPlay(), {
      width: bw, height: playH, fontSize: Phaser.Math.Clamp(Math.round(34 * s), 24, 38), icon: 'play',
    });
    this.tweens.add({ targets: play, scaleX: 1.035, scaleY: 1.035, duration: 1100, yoyo: true, repeat: -1, ease: 'Sine.inOut', delay: 700 });

    const gap = Math.max(10, w * 0.014);
    const halfW = (bw - gap) / 2;
    const leftX = colX - (halfW + gap) / 2;
    const rightX = colX + (halfW + gap) / 2;
    const fs = Phaser.Math.Clamp(Math.round(19 * s), 14, 22);
    const board = new Button(this, leftX, row1Y, t('menu.rankings'), () => this.scene.start('Leaderboard'), {
      width: halfW, height: secH, color: PALETTE.cadence, fontSize: fs,
    });
    const train = new Button(this, rightX, row1Y, t('menu.train'), () => this.scene.start('Training'), {
      width: halfW, height: secH, color: PALETTE.power, fontSize: fs,
    });
    const arena = new Button(this, leftX, row2Y, t('menu.arena'), () => this.scene.start('Arena'), {
      width: halfW, height: secH, color: PALETTE.warn, fontSize: fs,
    });
    const shop = new Button(this, rightX, row2Y, t('menu.shop'), () => this.scene.start('Cosmetics'), {
      width: halfW, height: secH, color: PALETTE.laneAccent, fontSize: fs,
    });

    // Staggered slide-in entrance for the action buttons.
    [play, board, train, arena, shop].forEach((b, i) => {
      const finalX = b.x;
      b.setX(finalX - w * 0.5).setAlpha(0);
      this.tweens.add({ targets: b, x: finalX, alpha: 1, duration: 460, delay: 150 + i * 90, ease: 'Cubic.out' });
    });
  }

  /** Native combo for the language, centred at the top — readable on mobile, shows the active one. */
  private languageSelect(s: number): void {
    const sel = document.createElement('select');
    for (const l of LANGS) {
      const o = document.createElement('option');
      o.value = l.code;
      o.textContent = `${l.flag}  ${l.name}`;
      if (l.code === getLang()) o.selected = true;
      sel.appendChild(o);
    }
    const fs = Math.max(15, Math.round(16 * s));
    Object.assign(sel.style, {
      position: 'fixed', left: '50%', top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
      transform: 'translateX(-50%)', zIndex: '40',
      fontSize: `${fs}px`, fontFamily: '"Twemoji Country Flags", system-ui, sans-serif', fontWeight: '700',
      padding: '7px 14px', borderRadius: '12px',
      border: '2px solid #59d2ff', background: 'rgba(11,16,32,0.92)', color: '#f4f7ff',
      outline: 'none', cursor: 'pointer', boxShadow: '0 3px 12px rgba(0,0,0,0.4)',
    } satisfies Partial<CSSStyleDeclaration>);
    sel.addEventListener('change', () => {
      SaveManager.instance.setLanguage(sel.value as Lang);
      this.scene.restart();
    });
    document.body.appendChild(sel);
    this.langSelect = sel;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { sel.remove(); if (this.langSelect === sel) this.langSelect = undefined; });
  }

  /** Soft left-side darkening so the buttons + logo read over the bright key-art sky. */
  private leftScrim(w: number, h: number): void {
    const g = this.add.graphics().setScrollFactor(0).setDepth(-20);
    const steps = 14;
    for (let i = 0; i < steps; i++) {
      const a = (1 - i / steps) * 0.34;
      g.fillStyle(PALETTE.bgBottom, a).fillRect((w * 0.52) * (i / steps), 0, w * 0.52 / steps + 1, h);
    }
    // Gentle top band for the chips.
    for (let i = 0; i < 10; i++) g.fillStyle(PALETTE.ink, (1 - i / 10) * 0.4).fillRect(0, (66 / 10) * i, w, 66 / 10 + 1);
  }

  /** Slow confetti drifting up the screen for ambient life. */
  private ambientParticles(w: number, h: number): void {
    if (!this.textures.exists('confetti')) return;
    this.add.particles(0, h + 10, 'confetti', {
      x: { min: 0, max: w }, y: h + 10,
      lifespan: 6000, speedY: { min: -40, max: -20 }, speedX: { min: -14, max: 14 },
      angle: { min: 0, max: 360 }, rotate: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0.3 }, alpha: { start: 0.0, end: 0.28 },
      frequency: 520, tint: [0xff7a59, 0x59d2ff, 0xffd24a, 0x4ade80],
    }).setDepth(-20).setScrollFactor(0);
  }

  private coverBackground(key: string): void {
    const img = this.add.image(0, 0, key).setOrigin(0, 0).setScrollFactor(0).setDepth(-100);
    const src = this.textures.get(key).getSourceImage();
    img.setScale(Math.max(this.scale.width / src.width, this.scale.height / src.height));
  }

  private chip(x: number, y: number, text: string, s: number, align: 0 | 1, onTap?: () => void): { right: number; height: number } {
    const fs = Math.round(Math.max(18, 20 * s));
    const t = this.add.text(0, 0, text, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: '#ffffff', fontStyle: 'bold' }).setShadow(0, 2, '#000000cc', 3);
    const padX = 16 * s;
    const w = t.width + padX * 2;
    const hh = t.height + 14 * s;
    const bx = align === 0 ? x : x - w;
    const g = this.add.graphics().setScrollFactor(0)
      .fillStyle(PALETTE.ink, 0.78).fillRoundedRect(bx, y, w, hh, hh / 2)
      .lineStyle(2, PALETTE.cadence, 0.6).strokeRoundedRect(bx, y, w, hh, hh / 2);
    t.setPosition(bx + padX, y + hh / 2).setOrigin(0, 0.5).setScrollFactor(0).setDepth(1);
    if (onTap) {
      g.setInteractive(new Phaser.Geom.Rectangle(bx, y, w, hh), Phaser.Geom.Rectangle.Contains);
      g.on(Phaser.Input.Events.POINTER_DOWN, onTap);
    }
    return { right: bx + w, height: hh };
  }

  /** Small round edit (pencil) button — sits next to the team chip and opens the Account screen. */
  private editBadge(x: number, y: number, s: number, onTap: () => void): void {
    const r = Math.max(15, 17 * s);
    const cyc = y;
    const c = this.add.circle(x + r, cyc, r, PALETTE.ink, 0.78).setScrollFactor(0).setStrokeStyle(2, PALETTE.cadence, 0.6).setDepth(1);
    const t = this.add.text(x + r, cyc, '✏️', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(15 * s)}px` }).setOrigin(0.5).setScrollFactor(0).setDepth(2);
    [c, t].forEach((o) => o.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, onTap));
  }

}
